"use client"

import { ReadonlyURLSearchParams } from "next/navigation"
import { trackFactFinderAddToCartAction, trackFactFinderProductClickAction } from "@/actions/factFinderTrackingActions"
import { sendGTMEvent } from "@next/third-parties/google"
import { ProductPriceContainerCtnrROV1 } from "@ub/pricing-client"

import { CardPrices, determineCardPrices } from "@/lib/price-utils"
import { ReducedProductData } from "@/lib/storefront/product/product"
import { calculateCategoryPathString, determineEffectiveProductId } from "@/lib/storefront/product/productHelper"
import { getPaginationSearchParams } from "@/components/search-and-category/pagination-info"

export function getSearchTermFromLocationOrSearchParams(searchParams: ReadonlyURLSearchParams) {
  const regex = /search\/([^\/]+)/
  const pathname = window.location.pathname
  const match = pathname.match(regex)
  if (match && match[1]) {
    return match[1]
  }
  return searchParams.get("searchTerm") ?? undefined
}

export function trackGTMAndFF(
  props: {
    trackingList?: string
    productData: ReducedProductData
    position: number
    page?: number
    pageSize?: number
    searchTerm?: string
  },
  searchParams: ReadonlyURLSearchParams
) {
  console.info("Tracking productClick")
  // Track in Google Tag Manager
  trackProductClickGTM(props.productData, props.trackingList ?? "", props.position)

  // Track in FactFinder using server action
  const searchTermFromLocation = getSearchTermFromLocationOrSearchParams(searchParams)
  const paginationSearchParams = getPaginationSearchParams(searchParams)

  if (props.trackingList) {
    trackFactFinderProductClickAction(
      props.productData,
      props.trackingList,
      props.position,
      searchTermFromLocation,
      paginationSearchParams.page,
      paginationSearchParams.pageSize
    ).catch((error) => console.error("Error calling trackFactFinderProductClickAction:", error))
  }
}

export function trackAddToBasket(
  isoLocale: string,
  isB2B: boolean,
  product: ReducedProductData | undefined,
  prices: ProductPriceContainerCtnrROV1 | undefined,
  quantity: number
) {
  if (product && prices && quantity) {
    //console.dir(prices, { depth: 10 })
    const cardPrices: CardPrices = determineCardPrices(isoLocale, isB2B, prices, undefined, quantity, product.id)
    //console.log("cardPrices for tracking")
    //console.dir(cardPrices)
    const addToCartData = {
      event: "addToCart",
      ecommerce: {
        currencyCode: product.currency,
        add: {
          products: [
            {
              name: product.title,
              id: determineEffectiveProductId(product.id),
              price: cardPrices.singleNetSalePrice,
              brand: product.brand,
              category: calculateCategoryPathString(product),
              quantity: "" + quantity,
            },
          ],
        },
      },
    }

    // Send it to Google Tag Manager
    sendGTMEvent(addToCartData)

    // Send to FactFinder using server action
    trackFactFinderAddToCartAction(
      product.id,
      quantity,
      cardPrices.singleNetSalePrice,
      product.title,
      product.productMasterSKU
    ).catch((error) => console.error("Error calling trackFactFinderAddToCartAction:", error))
  }
}

export function trackRemoveFromBasket(
  isoLocale: string,
  isB2B: boolean,
  product: ReducedProductData | undefined,
  prices: ProductPriceContainerCtnrROV1 | undefined,
  quantity: number
) {
  if (product && prices && quantity) {
    const cardPrices: CardPrices = determineCardPrices(isoLocale, isB2B, prices, undefined, quantity, product.id)

    const removeFromCartData = {
      event: "removeFromCart",
      ecommerce: {
        currencyCode: product.currency,
        add: {
          products: [
            {
              name: product.title,
              id: determineEffectiveProductId(product.id),
              price: cardPrices.salePrice,
              brand: product.brand,
              category: calculateCategoryPathString(product),
              quantity: "" + quantity,
            },
          ],
        },
      },
    }

    sendGTMEvent(removeFromCartData)
  }
}

export function trackFFProductClick(
  product: ReducedProductData | undefined,
  trackingList: string,
  position: number,
  searchTerm: string | undefined
) {
  if (product) {
    trackFactFinderProductClickAction(product, trackingList, position, searchTerm).then(() => {
      console.log("trackFFProductClick")
    })
  }
}

export function trackProductClickGTM(product: ReducedProductData, trackingList: string, position: number) {
  /*

//For reference: an original event json payload from legacy app

{
    "event": "productClick",
    "ecommerce": {
    "click": {
      "actionField": {
        "list": "Category Resultslist",
          "action": "click"
      },
      "products": [
        {
          "category": "Programm GW-Mailand",
          "id": "A254411",
          "list": "Category Resultslist",
          "name": "Elektrisch höhenverstellbarer Schreibtisch GW-MAILAND 7909",
          "position": 1
        }
      ]
    }
  },
    "gtm.uniqueEventId": 244
  }*/

  const trackedProductId = product.id.toLowerCase().startsWith("e") ? product.id.toUpperCase() : "A" + product.id

  const productClickData = {
    event: "productClick",
    ecommerce: {
      click: {
        actionField: {
          list: trackingList,
          action: "click",
        },
        products: [
          {
            category: product.category ?? "",
            id: trackedProductId,
            list: trackingList,
            name: product.title,
            position: position,
          },
        ],
      },
    },
  }

  // Send to Google Tag Manager only
  // FactFinder tracking is handled by the ProductClickTrackingClient component
  // which calls the trackProductClickAction server action
  sendGTMEvent(productClickData)
}
